/*
 * Column layout helpers.
 *
 * A tag column must never be resizable narrower than its widest pill *including*
 * the delete "×" that appears on hover. 
 * 
 * Otherwise hovering the rightmost pill grows it (+16px), 
 * which wraps it to the next line, 
 * which drops the hover,
 * which unwraps it — a miserable infinite flicker. 
 * 
 * Therefore, we compute a content-aware minimum so both the resize floor and the grid track can respect it.
 *
 * Geometry — keep in sync with CellRenderer.module.css:
 *   .tagPill padding           6px × 2                = 12   (PILL_PADDING_X)
 *   delete reveal on hover     max-width 12 + ml 4    = 16   (DELETE_REVEAL)
 *   .tagClickableArea pad-x    --cell-pad-x 5px × 2   = 10   (CELL_PADDING_X)
 */

const PILL_PADDING_X = 12;
const DELETE_REVEAL = 16;
const CELL_PADDING_X = 10;
const PILL_GAP = 6;            // horizontal gap between pills (.tagPillLine gap)
const SAFETY = 2;              // sub-pixel + font-load drift cushion
const DEFAULT_MIN_WIDTH = 50;  // the floor for a generic empty tag column

// One canvas context, reused across calls — spinning up a canvas per measure is wasteful.
// The font string mirrors .tagPill: font-weight 500, 13px, --font-body.
let _ctx = null;
const getCtx = () => {
    if (!_ctx) {
        _ctx = document.createElement('canvas').getContext('2d');
        _ctx.font = "500 13px 'Bricolage Grotesque', sans-serif";
    }
    return _ctx;
};

/*
 * Minimum resizable width (px) for a column, given the rows it spans.
 * Only 'tags' columns get a content-aware minimum; everything else — and an
 * empty tag column — falls back to the default floor.
 */
export const getColumnMinWidth = (column, rows = []) => {
    if (!column || column.type !== 'tags') return DEFAULT_MIN_WIDTH;

    const ctx = getCtx();
    let widestPill = 0;

    for (const row of rows) {
        const tags = row[column.id];
        if (!Array.isArray(tags)) continue;
        for (const tag of tags) {
            // Pill width at its widest — text + padding + the revealed delete button
            const pillWidth = ctx.measureText(tag).width + PILL_PADDING_X + DELETE_REVEAL;
            if (pillWidth > widestPill) widestPill = pillWidth;
        }
    }

    if (widestPill === 0) return DEFAULT_MIN_WIDTH; // no tags anywhere in the column
    return Math.max(DEFAULT_MIN_WIDTH, Math.ceil(widestPill + CELL_PADDING_X + SAFETY));
};

/*
 * Break a tag list into explicit lines for a given pill-area width, reserving
 * one delete-button width (DELETE_REVEAL) per line so the hovered pill can grow
 * without pushing anything past the edge.
 *
 * Returns an array of lines, each an array of tag strings (order preserved).
 */
export const packTagLines = (tags = [], availableWidth = 0) => {
    if (!Array.isArray(tags) || tags.length === 0) return [];

    const ctx = getCtx();
    const lines = [];
    let line = [];
    let lineWidth = 0; // running width of the pills + gaps already on this line

    for (const tag of tags) {
        const pillWidth = ctx.measureText(tag).width + PILL_PADDING_X;

        if (line.length === 0) {
            // The first pill always goes on the line
            // The min-width floor guarantees even the widest single pill fits with its delete button.
            line.push(tag);
            lineWidth = pillWidth;
            continue;
        }

        // Keep this pill on the line only if it still leaves room for the delete button
        const projected = lineWidth + PILL_GAP + pillWidth + DELETE_REVEAL;
        if (projected <= availableWidth) {
            line.push(tag);
            lineWidth += PILL_GAP + pillWidth;
        } else {
            lines.push(line);
            line = [tag];
            lineWidth = pillWidth;
        }
    }

    if (line.length) lines.push(line);
    return lines;
};
